import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, switchMap, take } from 'rxjs';
import { SavedDatabaseModel } from '../../models/saved/saved-database-model.type';
import { ToCreateDatabaseModel } from '../../models/to-create/to-create-database-model.type';
import { environment } from './../../../../../environments/environment';
import { AuthenticatedUser } from '../../../../core/model/user/authenticated-user.type';
import { AuthApiService } from '../../../../core/services/auth/auth-api.service';
import { FIRST_OF_OBSERVABLE } from '../../models/constant/constants.constant';

@Injectable({
  providedIn: 'root',
})
export class DatabaseModelApiService {
  private _http: HttpClient = inject(HttpClient);
  private _userService = inject(AuthApiService);

  private readonly _BASE_API_URL = environment.apiUrl;

  public getAllDatabaseModels$(): Observable<SavedDatabaseModel[]> {
    return this._userService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null) => {
        if (user) return this._http.get<SavedDatabaseModel[]>(this._BASE_API_URL + `/user/${user.id}/model/`);
        return of([]);
      })
    );
  }

  public getDatabaseModelById$(id: number): Observable<SavedDatabaseModel> {
    return this._userService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null | undefined) => {
        if (user) return this._http.get<SavedDatabaseModel>(this._BASE_API_URL + `/user/${user.id}/model/${id}`);
        return of();
      })
    );
  }

  public postDatabaseModel$(databaseModel: ToCreateDatabaseModel): Observable<SavedDatabaseModel | null> {
    return this._userService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null | undefined) => {
        if (user) return this._http.post<SavedDatabaseModel>(this._BASE_API_URL + `/user/${user.id}/model/`, databaseModel);
        return of(null);
      })
    );
  }

  public updateDatabaseModel$(databaseModel: SavedDatabaseModel): Observable<SavedDatabaseModel | null> {
    return this._userService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null | undefined) => {
        if (user) return this._http.put<SavedDatabaseModel>(this._BASE_API_URL + `/user/${user.id}/model/${databaseModel.id}`, databaseModel);
        return of(null);
      })
    );
  }

  public deleteDatabaseModel$(id: number): Observable<SavedDatabaseModel[]> {
    return this._userService
      .getUser$()
      .pipe(
        take(FIRST_OF_OBSERVABLE),
        switchMap((user: AuthenticatedUser | null | undefined) => {
          if (user) return this._http.delete<void>(this._BASE_API_URL + `/user/${user.id}/model/${id}`);
          return of();
        }),
        switchMap(() => this.getAllDatabaseModels$().pipe(take(FIRST_OF_OBSERVABLE))
        ));
  }
}
